import { useState } from "react";

const SendPdf = ({ createPdf, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleEmailChange = (event) => {
    const emailInput = event.target.value;
    setEmail(emailInput);
    const validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(validEmailRegex.test(emailInput));
    setErrorMessage("");
  };

  const handleSendPDFClick = async (event) => {
    event.preventDefault();
    if (!isEmailValid) return;

    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    // Adjust viewport for desktop simulation
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewportContent = viewportMeta.getAttribute("content");
    viewportMeta.setAttribute("content", "width=1279");

    const mainDoc = document.getElementById("root");
    mainDoc.classList.add("temp-pdf-width"); // Apply temporary width
    // Select the div and store its original style
    const scrollableDiv = document.querySelector(".overflow-auto");
    const originalStyle = scrollableDiv.getAttribute("style");

    // Expand the div to display all content
    scrollableDiv.style.overflow = "visible";
    scrollableDiv.style.maxHeight = "none";

    // get elements with class custom-select
    const customSelects = document.querySelectorAll(".custom-select");
    customSelects.forEach((select) => {
      select.classList.remove("custom-select");
    });

    // expand all h2 titles with data-section-title attribute and footer
    const sectionTitles = document.querySelectorAll("[data-section-title]");
    sectionTitles.forEach((title) => {
      title.classList.add("pb-6");
    });
    const footer = document.querySelector("footer");
    footer.classList.add("h-16");

    try {
      const generatedPdf = await createPdf();
      const pdfBlob = generatedPdf.output("blob");

      // Revert viewport and width
      scrollableDiv.setAttribute("style", originalStyle);
      customSelects.forEach((select) => {
        select.classList.add("custom-select");
      });
      viewportMeta.setAttribute("content", originalViewportContent);
      mainDoc.classList.remove("temp-pdf-width");

      // revert h2 classes and footer
      sectionTitles.forEach((title) => {
        title.classList.remove("pb-6");
      });
      footer.classList.remove("h-16");

      const formData = new FormData();
      formData.append("email", email);
      formData.append(
        "pdf",
        pdfBlob,
        `The V Simulator Report_${new Date().toISOString().slice(0, 10)}.pdf`
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/send-pdf`,
        {
          method: "POST",
          body: formData,
        }
      );

      setLoading(false);

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(
          result.message ||
            "Prepare to be inspired - your curated investment simulation report is on its way!"
        );
        // onHide();
      } else {
        setErrorMessage(`Failed to send PDF: ${response.status}`);
      }
    } catch (error) {
      // Ensure all styles and viewport are reset on error
      viewportMeta.setAttribute("content", originalViewportContent);
      mainDoc.classList.remove("temp-pdf-width");
      setLoading(false);
      setErrorMessage(`Error sending PDF: ${error.message}`);
    }
  };

  return (
    <div
      data-html2canvas-ignore
      id="pdf-modal"
      tabIndex="-1"
      aria-hidden="true"
      className="flex overflow-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)] max-h-full"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="relative p-4 mx-auto w-full max-w-2xl max-h-full">
        <div className="relative bg-white rounded-lg shadow">
          <div className="p-4 md:p-5 space-y-4 h-auto overflow-auto">
            <div className="w-full flex items-center justify-end pb-4">
              <h3 className="text-lg md:text-xl text-black font-semibold">
                Your Personalized V Simulator Report
              </h3>
              <button
                onClick={onHide}
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                data-modal-hide="default-modal"
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <form className="flex flex-col sm:flex-row gap-4 sm:gap-0 items-center text-xs lg:text-base w-full">
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email"
                  className={`input bg-white border border-gray-300 rounded p-2 h-10 lg:h-12 grow w-full sm:w-auto ${
                    errorMessage ? "border-[#CC0F0F]" : ""
                  }`}
                />
                <button
                  className={`sm:ml-2 text-white bg-[#1839D4] focus:ring-4 focus:outline-none focus:ring-blue-300 font-semibold rounded-[4px] text-[15px] px-6 py-3 text-center w-full sm:w-auto ${
                    !isEmailValid || loading
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={handleSendPDFClick}
                  disabled={!isEmailValid || loading}
                >
                  {loading ? "Sending..." : "Send PDF"}
                </button>
              </form>
              <div>
                {successMessage && (
                  <p className="text-[#65A509] font-medium text-xs">
                    ✓&nbsp;{successMessage}
                  </p>
                )}
                {errorMessage && (
                  <p className="text-[#CC0F0F] font-medium text-xs">
                    *&nbsp;{errorMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendPdf;
